/* eslint-disable pii/no-phone-number */
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Grid,
  Typography,
  Skeleton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Box,
} from '@mui/material';
import { find, forEach, includes, map, replace } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import VMasker from 'vanilla-masker';
import {
  Button,
  Checkbox,
  Input,
  PageWrapper,
  TimePicker,
} from '../../../components';
import { CONFIGURATION_UPDATE } from '../../../routes';
import {
  fetchConfigurationCreate,
  fetchConfigurationUpdate,
  fetchConfigurationsTypesList,
  useDispatch,
  useSelector,
} from '../../../stores';
import { theme } from '../../../theme';
import { handleErrors } from '../../../utils/Helpers';
import useResponsiveMode from '../../../utils/useResponsiveMode';
import { periods } from './constants';
import { pageSchema, priceSchema, operationPeriodSchema } from './validations';

export const ConfigurationUpsert: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    configurationsListLoading,
    configurationsTypesList,
    configurationsTypesListLoading,
  } = useSelector((state) => state.configurationsReducer);
  const { configurationInfo } = useSelector(
    (state) => state.configurationReducer,
  );
  const responsiveMode = useResponsiveMode();
  const location = useLocation();
  const [formData, setFormData] = useState<{
    id: number;
    name: string;
    prices: {
      vehicleCategory: string;
      timePeriod: string;
      price: string;
      errors: { [key: string]: string | undefined };
    }[];
    operationPeriods: {
      startTime: string;
      endTime: string;
      weekDays: {
        monday: boolean;
        tuesday: boolean;
        wednesday: boolean;
        thursday: boolean;
        friday: boolean;
        saturday: boolean;
        sunday: boolean;
      };
      errors: { [key: string]: string | undefined };
    }[];
  }>({
    id: 0,
    name: '',
    prices: [],
    operationPeriods: [],
  });
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>(
    {},
  );
  const [title, setTitle] = useState('Adicionar configuração');

  const addNewEmptyPriceRow = () => {
    const newPrices = formData.prices;
    newPrices.push({
      vehicleCategory: '',
      timePeriod: '',
      price: '',
      errors: {},
    });
    setFormData({
      ...formData,
      prices: newPrices,
    });
    setErrors({ ...errors, prices: '' });
  };

  const addNewEmptyWorkingPeriodRow = () => {
    const newOperationPeriods = formData.operationPeriods;
    newOperationPeriods.push({
      startTime: '',
      endTime: '',
      weekDays: {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
      },
      errors: {},
    });
    setFormData({
      ...formData,
      operationPeriods: newOperationPeriods,
    });
    setErrors({ ...errors, operationPeriods: '' });
  };

  useEffect(() => {
    dispatch(fetchConfigurationsTypesList());
    if (location.pathname === CONFIGURATION_UPDATE) {
      setTitle('Editar configuração');

      const handledPrices =
        configurationInfo && configurationInfo.prices
          ? map(configurationInfo.prices, (item) => {
            return {
              vehicleCategory: item.vehicleCategory,
              timePeriod: item.timePeriod,
              price: VMasker.toMoney(
                parseFloat(item.price).toFixed(2).toString(),
              ),
              errors: {},
            };
          })
          : [];

      const handledOperationPeriods =
        configurationInfo && configurationInfo.operationPeriods
          ? map(configurationInfo.operationPeriods, (item) => {
            return {
              startTime: item.startTime,
              endTime: item.endTime,
              weekDays: {
                monday: includes(item.weekDays, '1'),
                tuesday: includes(item.weekDays, '2'),
                wednesday: includes(item.weekDays, '3'),
                thursday: includes(item.weekDays, '4'),
                friday: includes(item.weekDays, '5'),
                saturday: includes(item.weekDays, '6'),
                sunday: includes(item.weekDays, '7'),
              },
              errors: {},
            };
          })
          : [];

      setFormData({
        id:
          configurationInfo && configurationInfo.id ? configurationInfo.id : 0,
        name:
          configurationInfo && configurationInfo.name
            ? configurationInfo.name
            : '',
        prices: handledPrices,
        operationPeriods: handledOperationPeriods,
      });
    }
  }, []);

  const periodDaysHasAtLeastOneTrue = (days: boolean[]) => {
    let hasAtLeastOneTrue = false;
    find(days, (day) => {
      if (day) hasAtLeastOneTrue = true;
    });
    return hasAtLeastOneTrue;
  };

  const handleSubmit = () => {
    setErrors({});
    let pricesHasErrors = false;
    let operationPeriodsHasErrors = false;

    forEach(formData.prices, (item: any, index: number) => {
      priceSchema.validate(item, { abortEarly: false }).catch((err) => {
        pricesHasErrors = true;
        const handledPrices = formData.prices;
        handledPrices[index].errors = handleErrors(err);
        setFormData({
          ...formData,
          prices: handledPrices,
        });
      });
    });
    forEach(formData.operationPeriods, (item: any, index: number) => {
      operationPeriodSchema
        .validate(item, { abortEarly: false })
        .catch((err) => {
          operationPeriodsHasErrors = true;
          const handledOperationPeriods = formData.operationPeriods;
          handledOperationPeriods[index].errors = handleErrors(err);
          if (!periodDaysHasAtLeastOneTrue(item.weekDays)) {
            handledOperationPeriods[index].errors = {
              ...handledOperationPeriods[index].errors,
              weekDays: 'Selecione pelo menos um dia',
            };
          }
          setFormData({
            ...formData,
            operationPeriods: handledOperationPeriods,
          });
        });
    });
    pageSchema
      .validate(formData, { abortEarly: false })
      .then(() => {
        if (!pricesHasErrors && !operationPeriodsHasErrors) {
          const handledPrices = map(formData.prices, (item) => {
            return {
              vehicleCategory: item.vehicleCategory,
              timePeriod: item.timePeriod,
              price: replace(item.price.replaceAll('.', ''), ',', '.'),
            };
          });
          const handledOperationPeriods: {
            startTime: string;
            endTime: string;
            weekDays: string[];
          }[] = map(formData.operationPeriods, (item) => {
            const weekDays = [];
            if (item.weekDays.monday) weekDays.push('1');
            if (item.weekDays.tuesday) weekDays.push('2');
            if (item.weekDays.wednesday) weekDays.push('3');
            if (item.weekDays.thursday) weekDays.push('4');
            if (item.weekDays.friday) weekDays.push('5');
            if (item.weekDays.saturday) weekDays.push('6');
            if (item.weekDays.sunday) weekDays.push('7');

            return {
              startTime: item.startTime,
              endTime: item.endTime,
              weekDays: weekDays,
            };
          });

          if (location.pathname === CONFIGURATION_UPDATE) {
            dispatch(
              fetchConfigurationUpdate({
                configurationId: formData.id.toString(),
                name: formData.name,
                prices: handledPrices,
                operationPeriods: handledOperationPeriods,
              }),
            ).then(() => {
              navigate(-1);
            });
          } else {
            dispatch(
              fetchConfigurationCreate({
                name: formData.name,
                prices: handledPrices,
                operationPeriods: handledOperationPeriods,
              }),
            ).then(() => {
              navigate(-1);
            });
          }
        }
      })
      .catch((err) => {
        setErrors(handleErrors(err));
      });
  };

  const handleReturn = () => {
    navigate(-1);
  };

  return (
    <PageWrapper>
      <Grid container justifyContent='space-between'>
        <Grid item lg={12} md={12} sm={12} xs={12} margin={1}>
          <Typography
            variant={responsiveMode !== 'phone' ? 'h4' : 'h5'}
            margin={1}
            display='flex'
            justifyContent='flex-start'
            alignItems='center'
            color={theme.palette.text.main}
          >
            <SettingsIcon fontSize='large' sx={{ marginRight: 2 }} />
            {title}
          </Typography>
        </Grid>
      </Grid>
      <Grid container margin={3}>
        {configurationsListLoading || configurationsTypesListLoading ? (
          <Skeleton animation='wave' width='100%' height='300px' />
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table
                // sx={{ minWidth: 500 }}
                size='small'
                aria-label='a dense table'
              >
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Box
                        sx={{
                          width: '100%',
                          padding: 2,
                          borderBottom: 0.1,
                          borderColor: theme.palette.divider,
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box flex={2} sx={{ maxWidth: '400px' }}>
                          <Input
                            data-testid='id'
                            type='text'
                            margin='normal'
                            label='Nome da configuração'
                            value={formData.name}
                            onChange={(value) => {
                              setErrors({ ...errors, name: '' });
                              setFormData({ ...formData, name: value });
                            }}
                            error={
                              errors &&
                              errors.name !== undefined &&
                              errors.name !== ''
                            }
                            helperText={errors && errors.name}
                          />
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            maxWidth: '300px',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                          }}
                        >
                          <Button
                            variant='contained'
                            label='Salvar'
                            startIcon={<SaveIcon />}
                            style={{ width: '100%' }}
                            // loading={areaCancelLoading}
                            onClick={handleSubmit}
                          />
                          <Button
                            color='error'
                            variant='contained'
                            startIcon={<ArrowBackIcon />}
                            label='Voltar'
                            style={{ width: '100%' }}
                            onClick={handleReturn}
                          />
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={4} sx={{ paddingTop: 5 }}>
                      <Button
                        variant='text'
                        startIcon={<AddCircleIcon />}
                        label='Novo período de cobrança'
                        style={{ margin: 0 }}
                        // loading={areaCancelLoading}
                        onClick={addNewEmptyWorkingPeriodRow}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography
                        color='primary.main'
                        sx={{ fontSize: '14px', fontWeight: 'bold' }}
                      >
                        Dias da semana
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: '120px' }}>
                      <Typography
                        color='primary.main'
                        sx={{ fontSize: '14px', fontWeight: 'bold' }}
                      >
                        Horário Inicial
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: '120px' }}>
                      <Typography
                        color='primary.main'
                        sx={{ fontSize: '14px', fontWeight: 'bold' }}
                      >
                        Horário Final
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.operationPeriods.length > 0 ? (
                    map(formData.operationPeriods, (item, index) => {
                      return (
                        <TableRow
                          sx={{
                            '&:nth-of-type(odd)': {
                              backgroundColor: theme.palette.action.hover,
                            },
                            '&:last-child td, &:last-child th': {
                              borderBottom: 0.1,
                              borderColor: theme.palette.divider,
                            },
                            height: '55px',
                          }}
                        >
                          <TableCell
                            component='th'
                            scope='row'
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'flex-start',
                              alignItems: 'center',
                            }}
                          >
                            <div
                              style={{
                                // height: '31.5px',
                                borderRadius: '4px',
                                paddingRight:
                                  responsiveMode === 'phone' ||
                                    responsiveMode === 'tablet'
                                    ? 0
                                    : '10px',
                                display:
                                  responsiveMode === 'phone' ||
                                    responsiveMode === 'tablet'
                                    ? 'block'
                                    : 'flex',
                                flexDirection: 'row',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                border:
                                  item.errors &&
                                    item.errors.weekDays !== undefined &&
                                    item.errors.weekDays !== ''
                                    ? '1px solid red'
                                    : 0,
                              }}
                            >
                              <Checkbox
                                label='Seg'
                                checked={
                                  formData.operationPeriods[index].weekDays
                                    .monday
                                }
                                onChange={() => {
                                  const handledOperationPeriods =
                                    formData.operationPeriods;
                                  handledOperationPeriods[index] = {
                                    ...item,
                                    weekDays: {
                                      ...item.weekDays,
                                      monday: !item.weekDays.monday,
                                    },
                                  };
                                  handledOperationPeriods[index].errors = {
                                    ...formData.operationPeriods[index].errors,
                                    weekDays: '',
                                  };
                                  setFormData({
                                    ...formData,
                                    operationPeriods: handledOperationPeriods,
                                  });
                                }}
                              />
                              <Checkbox
                                label='Ter'
                                checked={
                                  formData.operationPeriods[index].weekDays
                                    .tuesday
                                }
                                onChange={() => {
                                  const handledOperationPeriods =
                                    formData.operationPeriods;
                                  handledOperationPeriods[index] = {
                                    ...item,
                                    weekDays: {
                                      ...item.weekDays,
                                      tuesday: !item.weekDays.tuesday,
                                    },
                                  };
                                  handledOperationPeriods[index].errors = {
                                    ...formData.operationPeriods[index].errors,
                                    weekDays: '',
                                  };
                                  setFormData({
                                    ...formData,
                                    operationPeriods: handledOperationPeriods,
                                  });
                                }}
                              />
                              <Checkbox
                                label='Qua'
                                checked={
                                  formData.operationPeriods[index].weekDays
                                    .wednesday
                                }
                                onChange={() => {
                                  const handledOperationPeriods =
                                    formData.operationPeriods;
                                  handledOperationPeriods[index] = {
                                    ...item,
                                    weekDays: {
                                      ...item.weekDays,
                                      wednesday: !item.weekDays.wednesday,
                                    },
                                  };
                                  handledOperationPeriods[index].errors = {
                                    ...formData.operationPeriods[index].errors,
                                    weekDays: '',
                                  };
                                  setFormData({
                                    ...formData,
                                    operationPeriods: handledOperationPeriods,
                                  });
                                }}
                              />
                              <Checkbox
                                label='Qui'
                                checked={
                                  formData.operationPeriods[index].weekDays
                                    .thursday
                                }
                                onChange={() => {
                                  const handledOperationPeriods =
                                    formData.operationPeriods;
                                  handledOperationPeriods[index] = {
                                    ...item,
                                    weekDays: {
                                      ...item.weekDays,
                                      thursday: !item.weekDays.thursday,
                                    },
                                  };
                                  handledOperationPeriods[index].errors = {
                                    ...formData.operationPeriods[index].errors,
                                    weekDays: '',
                                  };
                                  setFormData({
                                    ...formData,
                                    operationPeriods: handledOperationPeriods,
                                  });
                                }}
                              />
                              <Checkbox
                                label='Sex'
                                checked={
                                  formData.operationPeriods[index].weekDays
                                    .friday
                                }
                                onChange={() => {
                                  const handledOperationPeriods =
                                    formData.operationPeriods;
                                  handledOperationPeriods[index] = {
                                    ...item,
                                    weekDays: {
                                      ...item.weekDays,
                                      friday: !item.weekDays.friday,
                                    },
                                  };
                                  handledOperationPeriods[index].errors = {
                                    ...formData.operationPeriods[index].errors,
                                    weekDays: '',
                                  };
                                  setFormData({
                                    ...formData,
                                    operationPeriods: handledOperationPeriods,
                                  });
                                }}
                              />
                              <Checkbox
                                label='Sáb'
                                checked={
                                  formData.operationPeriods[index].weekDays
                                    .saturday
                                }
                                onChange={() => {
                                  const handledOperationPeriods =
                                    formData.operationPeriods;
                                  handledOperationPeriods[index] = {
                                    ...item,
                                    weekDays: {
                                      ...item.weekDays,
                                      saturday: !item.weekDays.saturday,
                                    },
                                  };
                                  handledOperationPeriods[index].errors = {
                                    ...formData.operationPeriods[index].errors,
                                    weekDays: '',
                                  };
                                  setFormData({
                                    ...formData,
                                    operationPeriods: handledOperationPeriods,
                                  });
                                }}
                              />
                              <Checkbox
                                label='Dom'
                                checked={
                                  formData.operationPeriods[index].weekDays
                                    .sunday
                                }
                                onChange={() => {
                                  const handledOperationPeriods =
                                    formData.operationPeriods;
                                  handledOperationPeriods[index] = {
                                    ...item,
                                    weekDays: {
                                      ...item.weekDays,
                                      sunday: !item.weekDays.sunday,
                                    },
                                  };
                                  handledOperationPeriods[index].errors = {
                                    ...formData.operationPeriods[index].errors,
                                    weekDays: '',
                                  };
                                  setFormData({
                                    ...formData,
                                    operationPeriods: handledOperationPeriods,
                                  });
                                }}
                              />
                            </div>
                          </TableCell>
                          <TableCell component='th' scope='row'>
                            <TimePicker
                              value={formData.operationPeriods[index].startTime}
                              onChange={(value) => {
                                const handledOperationPeriods =
                                  formData.operationPeriods;
                                handledOperationPeriods[index].startTime =
                                  value;
                                handledOperationPeriods[index].errors = {
                                  ...formData.operationPeriods[index].errors,
                                  startTime: '',
                                };
                                setFormData({
                                  ...formData,
                                  operationPeriods: handledOperationPeriods,
                                });
                              }}
                              variant='outlined'
                              margin='none'
                              error={
                                item.errors &&
                                item.errors.startTime !== undefined &&
                                item.errors.startTime !== ''
                              }
                            />
                          </TableCell>
                          <TableCell
                            component='th'
                            scope='row'
                            sx={{ padding: 0 }}
                          >
                            <TimePicker
                              value={formData.operationPeriods[index].endTime}
                              onChange={(value) => {
                                const handledOperationPeriods =
                                  formData.operationPeriods;
                                handledOperationPeriods[index].endTime = value;
                                handledOperationPeriods[index].errors = {
                                  ...formData.operationPeriods[index].errors,
                                  endTime: '',
                                };
                                setFormData({
                                  ...formData,
                                  operationPeriods: handledOperationPeriods,
                                });
                              }}
                              variant='outlined'
                              margin='none'
                              error={
                                item.errors &&
                                item.errors.endTime !== undefined &&
                                item.errors &&
                                item.errors.endTime !== ''
                              }
                            />
                          </TableCell>
                          <TableCell
                            component='th'
                            scope='row'
                            align='right'
                            sx={{ width: '35px', padding: 0 }}
                          >
                            <IconButton
                              color='secondary'
                              aria-label='Remover linha'
                              onClick={() => {
                                const handledOperationPeriods =
                                  formData.operationPeriods;
                                handledOperationPeriods.splice(index, 1);
                                setFormData({
                                  ...formData,
                                  operationPeriods: handledOperationPeriods,
                                });
                              }}
                            >
                              <CancelIcon fontSize='large' />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: theme.palette.action.hover,
                        },
                        '&:last-child td, &:last-child th': {
                          borderBottom:
                            errors &&
                              errors.operationPeriods !== undefined &&
                              errors.operationPeriods !== ''
                              ? 2
                              : 1,
                          borderColor:
                            errors &&
                              errors.operationPeriods !== undefined &&
                              errors.operationPeriods !== ''
                              ? 'red'
                              : theme.palette.divider,
                        },
                      }}
                    >
                      <TableCell
                        align='center'
                        colSpan={4}
                        sx={{ height: '55px' }}
                      >
                        <Typography
                          sx={{
                            color:
                              errors &&
                                errors.operationPeriods !== undefined &&
                                errors.operationPeriods !== ''
                                ? 'red'
                                : 'grey',
                          }}
                        >
                          Nenhum período de cobrança inserido
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Table
                // sx={{ minWidth: 500 }}
                size='small'
                aria-label='a dense table'
                sx={{
                  border: errors && errors.prices ? '2px solid red' : undefined,
                  borderRadius: errors && errors.prices ? '4px' : undefined,
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={4} sx={{ paddingTop: 5 }}>
                      <Button
                        variant='text'
                        startIcon={<AddCircleIcon />}
                        label='Novo horário de funcionamento'
                        style={{ margin: 0 }}
                        // loading={areaCancelLoading}
                        onClick={addNewEmptyPriceRow}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography
                        color='primary.main'
                        sx={{ fontSize: '14px', fontWeight: 'bold' }}
                      >
                        Período
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        color='primary.main'
                        sx={{ fontSize: '14px', fontWeight: 'bold' }}
                      >
                        Preço
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        color='primary.main'
                        sx={{ fontSize: '14px', fontWeight: 'bold' }}
                      >
                        Categoria
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.prices.length > 0 ? (
                    map(formData.prices, (item, index) => {
                      return (
                        <TableRow
                          sx={{
                            '&:nth-of-type(odd)': {
                              backgroundColor: theme.palette.action.hover,
                            },
                            '&:last-child td, &:last-child th': {
                              borderBottom: 0.1,
                              borderColor: theme.palette.divider,
                            },
                            height: '55px',
                          }}
                        >
                          <TableCell
                            component='th'
                            scope='row'
                            sx={{ padding: '0 5px 0 20px' }}
                          >
                            <Select
                              value={formData.prices[index].timePeriod}
                              onChange={(value) => {
                                const handledPrices = formData.prices;
                                handledPrices[index] = {
                                  ...item,
                                  timePeriod: value.target.value,
                                };
                                handledPrices[index].errors = {
                                  ...formData.prices[index].errors,
                                  timePeriod: '',
                                };
                                setFormData({
                                  ...formData,
                                  prices: handledPrices,
                                });
                              }}
                              displayEmpty
                              size='small'
                              margin='none'
                              variant='outlined'
                              fullWidth
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: '300px',
                                  },
                                },
                              }}
                              error={
                                item.errors &&
                                item.errors.timePeriod !== undefined &&
                                item.errors.timePeriod !== ''
                              }
                            >
                              <MenuItem disabled value=''>
                                <Typography style={{ color: 'grey' }}>
                                  Selecione...
                                </Typography>
                              </MenuItem>
                              {map(periods, (item: string, pos: number) => {
                                return (
                                  <MenuItem
                                    key={item + pos}
                                    value={item}
                                    disabled={item === '1000'}
                                  >
                                    {item}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </TableCell>
                          <TableCell
                            component='th'
                            scope='row'
                            sx={{ padding: '0 5px 0 15px' }}
                          >
                            <TextField
                              placeholder='0,00'
                              size='small'
                              variant='outlined'
                              margin='none'
                              value={formData.prices[index].price}
                              onChange={(e) => {
                                const handledPrices = formData.prices;
                                handledPrices[index].price = VMasker.toMoney(
                                  e.target.value,
                                );
                                handledPrices[index].errors = {
                                  ...formData.prices[index].errors,
                                  price: '',
                                };
                                setFormData({
                                  ...formData,
                                  prices: handledPrices,
                                });
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position='start'>
                                    R$
                                  </InputAdornment>
                                ),
                              }}
                              fullWidth
                              error={
                                item.errors &&
                                item.errors.price !== undefined &&
                                item.errors.price !== ''
                              }
                            />
                          </TableCell>
                          <TableCell
                            component='th'
                            scope='row'
                            sx={{ padding: '0 0 0 15px' }}
                          >
                            <Select
                              value={formData.prices[index].vehicleCategory}
                              onChange={(value) => {
                                const handledPrices = formData.prices;
                                handledPrices[index] = {
                                  ...item,
                                  vehicleCategory: value.target.value,
                                };
                                handledPrices[index].errors = {
                                  ...formData.prices[index].errors,
                                  vehicleCategory: '',
                                };
                                setFormData({
                                  ...formData,
                                  prices: handledPrices,
                                });
                              }}
                              displayEmpty
                              size='small'
                              margin='none'
                              variant='outlined'
                              fullWidth
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: '300px',
                                  },
                                },
                              }}
                              error={
                                item.errors &&
                                item.errors.vehicleCategory !== undefined &&
                                item.errors.vehicleCategory !== ''
                              }
                            >
                              <MenuItem disabled value=''>
                                <Typography style={{ color: 'grey' }}>
                                  Selecione...
                                </Typography>
                              </MenuItem>
                              {map(
                                configurationsTypesList?.typePrices,
                                (item: string, pos: number) => {
                                  return (
                                    <MenuItem
                                      key={item + pos}
                                      value={item}
                                      disabled={item === '1000'}
                                    >
                                      {item}
                                    </MenuItem>
                                  );
                                },
                              )}
                            </Select>
                          </TableCell>
                          <TableCell
                            component='th'
                            scope='row'
                            align='right'
                            sx={{ width: '35px', padding: 0 }}
                          >
                            <IconButton
                              color='secondary'
                              aria-label='Remover linha'
                              onClick={() => {
                                const handledPrices = formData.prices;
                                handledPrices.splice(index, 1);
                                setFormData({
                                  ...formData,
                                  prices: handledPrices,
                                });
                              }}
                            >
                              <CancelIcon fontSize='large' />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: theme.palette.action.hover,
                        },
                        '&:last-child td, &:last-child th': {
                          borderBottom:
                            errors &&
                              errors.prices !== undefined &&
                              errors.prices !== ''
                              ? 2
                              : 1,
                          borderColor:
                            errors &&
                              errors.prices !== undefined &&
                              errors.prices !== ''
                              ? 'red'
                              : theme.palette.divider,
                        },
                      }}
                    >
                      <TableCell
                        align='center'
                        colSpan={4}
                        sx={{ height: '55px' }}
                      >
                        <Typography
                          sx={{
                            color:
                              errors &&
                                errors.prices !== undefined &&
                                errors.prices !== ''
                                ? 'red'
                                : 'grey',
                          }}
                        >
                          Nenhum horário de funcionamento inserido
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {/* Error message for prices */}
                  {errors && errors.prices && (
                    <TableRow>
                      <TableCell colSpan={4} align='center'>
                        <Typography sx={{ color: 'red', fontSize: '0.85rem' }}>
                          {errors.prices}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Grid>
    </PageWrapper>
  );
};
